import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n/I18nProvider';
import { useRouter } from '../../app/router';
import { createJsonFetch, moduleAction, previewImport } from './settings.api';
import type {
  ApprovalRoute,
  AuditEntry,
  ConfigPackage,
  Dimension,
  JsonFetch,
  MergePreview,
  ModuleAction,
  ModuleRefusal,
  ModuleView,
  NumberingRule,
  PostingRule,
  RulePreview,
  UserRecord,
} from './settings.api';
import { DimensionsView, ModulesView, NumberingView, PostingRulesView } from './sections/KernelSections';
import { ApprovalRoutesView, AuditView, PackagesView, UsersView } from './sections/GovernanceSections';
import { useResource } from './settings.hooks';
import './settings.css';

const SECTIONS = [
  'modules',
  'dimensions',
  'postingRules',
  'numbering',
  'approvalRoutes',
  'users',
  'packages',
  'audit',
] as const;

type Section = (typeof SECTIONS)[number];

const SCREEN_LINKS = [
  { path: '/document-types', key: 'settings.nav.documentTypes' },
  { path: '/document-chains', key: 'settings.nav.documentChains' },
  { path: '/print-templates', key: 'settings.nav.printTemplates' },
] as const;

function useSettingsFetch(): JsonFetch {
  const { authorizedFetch } = useAuth();
  return useMemo(() => createJsonFetch(authorizedFetch), [authorizedFetch]);
}

function SectionFrame({ children }: { children: ReactNode }): JSX.Element {
  return <section className="settings__panel-host">{children}</section>;
}

function ModulesSection({ fetchJson }: { fetchJson: JsonFetch }): JSX.Element {
  const { t } = useI18n();
  const { session } = useAuth();
  const { state, reload } = useResource<ModuleView[]>(fetchJson, '/modules');
  const [refusal, setRefusal] = useState<ModuleRefusal | null>(null);

  const actorId = session?.user.id;
  const actorRole = session?.user.roles[0];

  const act = (code: string, action: ModuleAction, reason?: string): void => {
    if (actorId === undefined) {
      return;
    }
    setRefusal(null);
    moduleAction(fetchJson, code, action, {
      actor: { id: actorId, ...(actorRole === undefined ? {} : { role: actorRole }) },
      ...(reason === undefined ? {} : { reason }),
    })
      .then(() => reload())
      .catch((cause: unknown) => {
        const message = cause instanceof Error ? cause.message : String(cause);
        setRefusal({ code: t('settings.actionFailed'), detail: message });
      });
  };

  if (state.status === 'loading') {
    return (
      <SectionFrame>
        <p className="muted">{t('settings.loading')}</p>
      </SectionFrame>
    );
  }
  if (state.status === 'error') {
    return (
      <SectionFrame>
        <p className="alert alert--danger" role="alert">
          {state.message}
        </p>
      </SectionFrame>
    );
  }
  return (
    <SectionFrame>
      <ModulesView modules={state.data} refusal={refusal} onAction={act} />
    </SectionFrame>
  );
}

function DimensionsSection({ fetchJson }: { fetchJson: JsonFetch }): JSX.Element {
  const { t } = useI18n();
  const { state } = useResource<Dimension[]>(fetchJson, '/dimensions');
  if (state.status === 'loading') {
    return (
      <SectionFrame>
        <p className="muted">{t('settings.loading')}</p>
      </SectionFrame>
    );
  }
  if (state.status === 'error') {
    return (
      <SectionFrame>
        <DimensionsView dimensions={[]} error={state.message} />
      </SectionFrame>
    );
  }
  return (
    <SectionFrame>
      <DimensionsView dimensions={state.data} />
    </SectionFrame>
  );
}

function PostingRulesSection({ fetchJson }: { fetchJson: JsonFetch }): JSX.Element {
  const { t } = useI18n();
  const { state } = useResource<PostingRule[]>(fetchJson, '/posting-rules');
  if (state.status === 'loading') {
    return (
      <SectionFrame>
        <p className="muted">{t('settings.loading')}</p>
      </SectionFrame>
    );
  }
  if (state.status === 'error') {
    return (
      <SectionFrame>
        <PostingRulesView rules={[]} error={state.message} />
      </SectionFrame>
    );
  }
  return (
    <SectionFrame>
      <PostingRulesView
        rules={state.data}
        onPreview={(rule) =>
          fetchJson<RulePreview>('/posting-rules/preview', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ rule }),
          })
        }
      />
    </SectionFrame>
  );
}

function NumberingSection({ fetchJson }: { fetchJson: JsonFetch }): JSX.Element {
  const { t } = useI18n();
  const { state } = useResource<NumberingRule[]>(fetchJson, '/numbering');
  if (state.status === 'loading') {
    return (
      <SectionFrame>
        <p className="muted">{t('settings.loading')}</p>
      </SectionFrame>
    );
  }
  if (state.status === 'error') {
    return (
      <SectionFrame>
        <NumberingView rules={[]} error={state.message} />
      </SectionFrame>
    );
  }
  return (
    <SectionFrame>
      <NumberingView rules={state.data} />
    </SectionFrame>
  );
}

function ApprovalRoutesSection({ fetchJson }: { fetchJson: JsonFetch }): JSX.Element {
  const { t } = useI18n();
  const { state } = useResource<ApprovalRoute[]>(fetchJson, '/approval-routes');
  if (state.status === 'loading') {
    return (
      <SectionFrame>
        <p className="muted">{t('settings.loading')}</p>
      </SectionFrame>
    );
  }
  if (state.status === 'error') {
    return (
      <SectionFrame>
        <ApprovalRoutesView routes={[]} error={state.message} />
      </SectionFrame>
    );
  }
  return (
    <SectionFrame>
      <ApprovalRoutesView routes={state.data} />
    </SectionFrame>
  );
}

function UsersSection({ fetchJson }: { fetchJson: JsonFetch }): JSX.Element {
  const { t } = useI18n();
  const { state } = useResource<UserRecord[]>(fetchJson, '/users');
  if (state.status === 'loading') {
    return (
      <SectionFrame>
        <p className="muted">{t('settings.loading')}</p>
      </SectionFrame>
    );
  }
  if (state.status === 'error') {
    return (
      <SectionFrame>
        <UsersView users={[]} error={state.message} />
      </SectionFrame>
    );
  }
  return (
    <SectionFrame>
      <UsersView users={state.data} />
    </SectionFrame>
  );
}

function PackagesSection({ fetchJson }: { fetchJson: JsonFetch }): JSX.Element {
  const { t } = useI18n();
  const { state } = useResource<ConfigPackage>(fetchJson, '/settings/export?name=AZ-Base');
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = async (pkg: ConfigPackage): Promise<void> => {
    try {
      setPreview(await previewImport(fetchJson, pkg));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  };

  if (state.status === 'loading') {
    return (
      <SectionFrame>
        <p className="muted">{t('settings.loading')}</p>
      </SectionFrame>
    );
  }
  if (state.status === 'error') {
    return (
      <SectionFrame>
        <PackagesView package={null} error={state.message} />
      </SectionFrame>
    );
  }
  return (
    <SectionFrame>
      <PackagesView package={state.data} preview={preview} error={error} />
      <div className="settings__actions">
        <button type="button" className="btn" onClick={() => void check(state.data)}>
          {t('settings.packages.check')}
        </button>
      </div>
    </SectionFrame>
  );
}

function AuditSection({ fetchJson }: { fetchJson: JsonFetch }): JSX.Element {
  const { t } = useI18n();
  const { state } = useResource<AuditEntry[]>(fetchJson, '/audit');
  if (state.status === 'loading') {
    return (
      <SectionFrame>
        <p className="muted">{t('settings.loading')}</p>
      </SectionFrame>
    );
  }
  if (state.status === 'error') {
    return (
      <SectionFrame>
        <AuditView entries={[]} error={state.message} />
      </SectionFrame>
    );
  }
  return (
    <SectionFrame>
      <AuditView entries={state.data} />
    </SectionFrame>
  );
}

function SectionBody({ section, fetchJson }: { section: Section; fetchJson: JsonFetch }): JSX.Element {
  switch (section) {
    case 'modules':
      return <ModulesSection fetchJson={fetchJson} />;
    case 'dimensions':
      return <DimensionsSection fetchJson={fetchJson} />;
    case 'postingRules':
      return <PostingRulesSection fetchJson={fetchJson} />;
    case 'numbering':
      return <NumberingSection fetchJson={fetchJson} />;
    case 'approvalRoutes':
      return <ApprovalRoutesSection fetchJson={fetchJson} />;
    case 'users':
      return <UsersSection fetchJson={fetchJson} />;
    case 'packages':
      return <PackagesSection fetchJson={fetchJson} />;
    case 'audit':
      return <AuditSection fetchJson={fetchJson} />;
  }
}

export default function SettingsScreen(): JSX.Element {
  const { t } = useI18n();
  const { navigate } = useRouter();
  const fetchJson = useSettingsFetch();
  const [active, setActive] = useState<Section>('modules');

  return (
    <div className="settings">
      <header className="page-head settings__head">
        <div>
          <h1>{t('settings.title')}</h1>
          <p className="muted">{t('settings.subtitle')}</p>
        </div>
      </header>

      <div className="settings__layout">
        <nav className="settings__nav" aria-label={t('settings.navLabel')}>
          {SECTIONS.map((section) => (
            <button
              key={section}
              type="button"
              className={section === active ? 'settings__nav-item is-active' : 'settings__nav-item'}
              data-testid={`settings-nav-${section}`}
              onClick={() => setActive(section)}
            >
              {t(`settings.nav.${section}`)}
            </button>
          ))}
          <span className="settings__nav-sep" />
          {SCREEN_LINKS.map((link) => (
            <button key={link.path} type="button" className="settings__nav-item" onClick={() => navigate(link.path)}>
              {t(link.key)}
            </button>
          ))}
        </nav>

        <div className="settings__content">
          <SectionBody section={active} fetchJson={fetchJson} />
        </div>
      </div>
    </div>
  );
}
